// ═══════════════════════════════════════════════════════════════
// VersionWatcher — Hybrid update UX
//
// Starts the version poller and, when a new deploy is detected,
// shows a non-blocking persistent CARD AND arms a safe auto-reload
// for the next screen change (handled in AppWithJobSync's nav
// handlers via consumeReloadIfPending). "Update now" reloads
// immediately. A reload never logs the user out — the Supabase
// session in localStorage is preserved.
//
// The card says what changed since the build this tab is running:
// one change reads as a line, several collapse into "N updates"
// that expands into a scrolling list. Lines are deliberately high
// level ("HR — fix") — see scripts/release-notes.mjs.
//
// It owns its own box rather than riding a sonner toast: sonner
// pins a toast to the height it measured at mount, so a list that
// grows on click spills straight out of the card. Toasts still get
// to stack ON TOP — the card publishes its height as
// --mos-update-card-h and <Toaster> lifts its stack by that much.
//
// Depends on no provider, so it lives at the App root (next to
// <Toaster>) and shows on the login screen too.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, RefreshCw, X } from 'lucide-react';
import {
  startVersionWatch,
  subscribe,
  getUpdateAvailable,
  getUpdateSummary,
  requestReloadOnNextNav,
  reloadNow,
} from '../../utils/versionCheck';
import { CHUNK_RELOAD_GUARD_KEY } from '../../utils/version';

/** Longest list we'll draw before folding the tail into a "+N more" line. */
const MAX_LISTED = 12;

/**
 * Read by <Toaster offset>: how much of the bottom-right corner the card takes
 * up, measured from the viewport edge — our own inset + our height + the gap a
 * toast should keep above us. Toasts lift by exactly this much.
 */
const CARD_SPACE_VAR = '--mos-update-card-space';

/** Matches `bottom-6` on the card shell. */
const CARD_INSET = 24;

/** Breathing room between the top of the card and the toast above it. */
const CARD_GAP = 12;

type UpdateSummary = { count: number; lines: string[] };

function UpdateCard({ count, lines, onDismiss }: UpdateSummary & { onDismiss: () => void }) {
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Publish our height so the sonner stack sits above the card instead of
  // under it. Re-measured on expand/collapse, cleared when we go away.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => {
      const space = CARD_INSET + Math.ceil(el.getBoundingClientRect().height) + CARD_GAP;
      root.style.setProperty(CARD_SPACE_VAR, `${space}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(CARD_SPACE_VAR);
    };
  }, []);

  const listed = lines.slice(0, MAX_LISTED);
  const hidden = lines.length - listed.length;

  return (
    <div
      ref={cardRef}
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[115] flex max-h-[calc(100vh-8rem)] w-[min(380px,calc(100vw-3rem))] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        <span className="mt-px grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg bg-[#2C2A29]">
          <RefreshCw className="h-3.5 w-3.5 text-white" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-snug font-semibold text-gray-900">
            A new version of MOS is available.
          </p>
          <p className="mt-0.5 text-[11px] leading-[1.4] text-gray-400">
            Updates on its own when you switch screens or step away.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mt-0.5 -mr-1 grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {/* One change reads as a line; several collapse behind the count. */}
      {lines.length === 1 && (
        <p className="px-4 pb-2 text-[12px] leading-[1.45] break-words text-gray-600">{lines[0]}</p>
      )}

      {lines.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex w-full items-center gap-1.5 px-4 py-1.5 text-left text-[12px] font-medium text-gray-500 transition-colors hover:text-gray-700"
          >
            {count} updates
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
          {open && (
            <div className="max-h-[240px] min-h-0 overflow-y-auto border-t border-gray-100 px-4 py-2">
              <ul className="list-disc space-y-1.5 pl-4 text-[12px] leading-[1.45] text-gray-600">
                {listed.map((line, i) => (
                  <li key={i} className="break-words">{line}</li>
                ))}
                {hidden > 0 && <li className="list-none pl-0 text-gray-400">+{hidden} more</li>}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-2.5">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-gray-600 transition-colors hover:bg-gray-100"
        >
          Later
        </button>
        <button
          type="button"
          onClick={() => reloadNow()}
          className="rounded-lg bg-[#2C2A29] px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#3a3836]"
        >
          Update now
        </button>
      </div>
    </div>
  );
}

export function VersionWatcher() {
  const [info, setInfo] = useState<UpdateSummary | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    startVersionWatch();

    // We mounted cleanly → chunks loaded fine → release the one-shot
    // chunk-reload guard so a FUTURE deploy can self-heal once again.
    const guardTimer = setTimeout(() => {
      try { sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY); } catch { /* ignore */ }
    }, 4000);

    const sync = () => {
      if (!getUpdateAvailable()) return;
      requestReloadOnNextNav();
      const next = getUpdateSummary();
      // Keep the same object when nothing changed: identity is what tells the
      // effect below whether this is a NEW deploy or the same one re-reported.
      setInfo((prev) =>
        prev && prev.count === next.count && prev.lines.join('\n') === next.lines.join('\n')
          ? prev
          : next,
      );
    };

    sync(); // in case an update was already detected before we subscribed
    const unsubscribe = subscribe(sync);

    return () => {
      clearTimeout(guardTimer);
      unsubscribe();
    };
  }, []);

  // A later deploy landing while the tab is open is a fresh event — bring the
  // card back even if the user waved the last one away.
  useEffect(() => {
    if (info) setDismissed(false);
  }, [info]);

  if (!info || dismissed) return null;

  return <UpdateCard count={info.count} lines={info.lines} onDismiss={() => setDismissed(true)} />;
}
